<template>
  <ModalContainer queryParam="result" ref="modalContainer" size="lg">
    <b-card>
      <h4 class="modal-title mb-2">Subir resultado de laboratorio</h4>

      <p v-if="nombrePaciente" class="mb-2">
        <strong>Paciente:</strong> {{ nombrePaciente }}
      </p>

      <LoadFileInput
        v-model="archivoResultado"
        :urlFile="urlInforme"
        label="Informe de laboratorio (PDF)"
        accept="application/pdf,.pdf"
        ref="inputResultado"
      />

      <div class="mt-2">
        <label class="d-block font-weight-bold mb-1">Fecha de resultados</label>
        <flat-pickr
          v-model="fechaResultado"
          class="form-control"
          :config="flatpickrConfig"
          placeholder="Seleccione fecha y hora"
        />
      </div>

      <div class="d-flex justify-content-end mt-2">
        <b-button
          class="mr-1"
          variant="secondary"
          :disabled="isSaving"
          @click="onClose"
        >
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          :disabled="isDisabledSave"
          @click="onSaveResultado"
        >
          {{ isSaving ? 'Guardando...' : 'Guardar' }}
        </b-button>
      </div>
    </b-card>
  </ModalContainer>
</template>

<script>
import { ModalContainer, LoadFileInput } from '@/shared/components';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import { labAuxiliarService } from '../../services';
import { fileService } from '@/shared/services';

export default {
  name: 'ModalResultadosLabAux',

  components: {
    ModalContainer,
    LoadFileInput,
    flatPickr,
  },

  data() {
    return {
      tokenAuxiliar: null,
      archivoResultado: null,
      urlInforme: null,
      identificacionPaciente: null,
      nombrePaciente: null,
      isSaving: false,
      fechaResultado: null,
      flatpickrConfig: {
        enableTime: true,
        dateFormat: 'Y-m-d H:i',
        time_24hr: true,
      },
    };
  },

  created() {
    this.loadData();
  },

  computed: {
    isDisabledSave() {
      const tieneArchivo = Boolean(this.archivoResultado) || Boolean(this.urlInforme);
      return this.isSaving || !tieneArchivo || !this.fechaResultado;
    },
  },

  methods: {
    async loadData() {
      this.archivoResultado = null;
      this.fechaResultado = null;
      this.tokenAuxiliar = this.$route.query.result;

      if (!this.tokenAuxiliar) return;

      const responseSearch = await labAuxiliarService.getAll({ tokenLaboratorio: this.tokenAuxiliar });

      if (!responseSearch || !responseSearch.data?.length) {
        await this.$swal({
          icon: 'error',
          title: 'No se encontró el auxiliar',
          showConfirmButton: true,
        });
        this.onClose();
        return;
      }

      const [auxiliar] = responseSearch.data;
      const paciente = auxiliar.atencionMedica?.cita?.paciente?.persona;

      if (paciente) {
        this.identificacionPaciente = paciente.identificacion;
        this.nombrePaciente = `${paciente.identificacion} - ${paciente.primerApellido} ${paciente.primerNombre}`;
      } else {
        this.identificacionPaciente = 'laboratorio';
        this.nombrePaciente = null;
      }

      this.urlInforme = auxiliar.urlInforme;

      if (auxiliar.fechaResultado) {
        this.fechaResultado = auxiliar.fechaResultado;
      }

      this.$nextTick(() => {
        if (!this.urlInforme && this.$refs.inputResultado && this.$refs.inputResultado.onEditFile) {
          this.$refs.inputResultado.onEditFile();
        }
      });
    },

    async onSaveResultado() {
      if (this.isDisabledSave) return;

      const confirmation = await this.$swal({
        title: '¿Deseas guardar el resultado?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, guardar',
        cancelButtonText: 'Cancelar',
      });

      if (!confirmation.isConfirmed) return;

      this.isSaving = true;

      let urlPdf = this.urlInforme;

      if (this.archivoResultado) {
        urlPdf = await fileService.upload(
          'resultadosLabAux',
          this.archivoResultado,
          this.identificacionPaciente || 'laboratorio',
          this.urlInforme,
        );

        if (!urlPdf) {
          this.isSaving = false;
          await this.$swal({
            icon: 'error',
            title: 'No se pudo cargar el archivo',
            showConfirmButton: true,
          });
          return;
        }
      }

      const fechaSeleccionada = this.fechaResultado instanceof Date
        ? this.fechaResultado
        : new Date(this.fechaResultado);

      if (Number.isNaN(fechaSeleccionada.getTime())) {
        this.isSaving = false;
        await this.$swal({
          icon: 'error',
          title: 'Fecha de resultados inválida',
          showConfirmButton: true,
        });
        return;
      }

      const fieldsToUpdate = {
        estadoLaboratorio: '4',
        urlInforme: urlPdf,
        fechaResultado: fechaSeleccionada.toISOString(),
      };

      const responseUpdate = await labAuxiliarService.updateByToken(this.tokenAuxiliar, fieldsToUpdate);
      this.isSaving = false;

      if (!responseUpdate) {
        await this.$swal({
          icon: 'error',
          title: 'No se pudo actualizar el auxiliar',
          showConfirmButton: true,
        });
        return;
      }

      if (
        Object.prototype.hasOwnProperty.call(responseUpdate, 'labAuxiliar')
        && responseUpdate.labAuxiliar === null
      ) {
        await this.$swal({
          icon: 'error',
          title: responseUpdate.message || 'No se pudo actualizar el auxiliar',
          showConfirmButton: true,
        });
        return;
      }

      await this.$swal({
        icon: 'success',
        title: 'Resultado cargado',
        showConfirmButton: true,
      });

      this.onClose();
      this.$router.go();
    },

    onClose() {
      this.archivoResultado = null;
      this.fechaResultado = null;
      if (this.$refs.modalContainer) {
        this.$refs.modalContainer.closeModal();
      }
    },
  },

  watch: {
    '$route.query.result': {
      handler() {
        this.loadData();
      },
    },
  },
};
</script>
